import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { getMetricColor, getQuantityTotal, hexToRgba, isQuantityBreakdown, kFormatter, METRIC } from "./calendarUtils";
import "./AllDates.scss";
import { formatCardDate } from "src/pages/providerPayRequestDashboard/utils/dateUtils";

const AllDates = ({ metricType, workCalendarData, year, providerId, currentDate, scope = {} }) => {
  const buttonColor = getMetricColor(metricType);
  const [expandedId, setExpandedId] = useState(null);
  const history = useHistory();

  const contractTypeColorMap = {
    "On Call": "#EC5757",
    "Medical Directorship": "#BA68C8",
    "Admin Services": "#0077B6",
  };

  const shouldDisplayMetric = (dateObj) => {
    if (metricType.value === METRIC.TOTAL_AMOUNT) {
      return (dateObj.budget || 0) > 0;
    } else if (metricType.value === METRIC.HOURS) {
      return (dateObj.hours || 0) > 0 || (dateObj.minutes || 0) > 0;
    } else if (metricType.value === METRIC.COUNT) {
      return (dateObj.timesheetCount || dateObj.count || 0) > 0;
    } else if (metricType.value === METRIC.QUANTITY) {
      return getQuantityTotal(dateObj.quantity) > 0;
    }
    return true;
  };

  const getContractTypeColor = (contractTypeName) => {
    return contractTypeColorMap[contractTypeName] || buttonColor;
  };

  const handleViewAllClick = () => {
    history.push({
      pathname: "/pay_request_detail",
      state: {
        provider_id: providerId,
        provider_ids: scope.in_provider_ids ?? null,
        org_position_keys: scope.in_org_position_keys ?? null,
        periodFilter: year ? "YTD" : "Monthly",
        currentMonth: (currentDate || new Date()).toISOString(),
      },
    });
  };

  const handleContractTypeClick = (dateObj, contractTypeMstrKey, extraState = {}) => {
    const dateId = dateObj.id || dateObj.title;
    history.push({
      pathname: "/pay_request_detail",
      state: {
        provider_id: providerId,
        provider_ids: scope.in_provider_ids ?? null,
        org_position_keys: scope.in_org_position_keys ?? null,
        startDate: dateId,
        endDate: dateId,
        contract_type_mstr_key: contractTypeMstrKey,
        ...extraState,
        periodFilter: year ? "YTD" : "Monthly",
        currentMonth: (currentDate || new Date()).toISOString(),
      },
    });
  };

  let datesArray = [];

  if (workCalendarData instanceof Map) {
    datesArray = Array.from(workCalendarData.entries()).map(([key, value]) => ({
      ...value,
      id: key,
    }));
  } else if (Array.isArray(workCalendarData)) {
    datesArray = workCalendarData;
  }

  datesArray = datesArray.filter((date) => {
    const dateWithFiltered = {
      ...date,
      contractTypes: (date.contractTypes || []).filter((item) => shouldDisplayMetric(item)),
    };
    return shouldDisplayMetric(date) || dateWithFiltered.contractTypes.length > 0;
  });

  const handleAction = (id, e) => {
    setExpandedId((prevId) => (prevId === id ? null : id));
  };

  const formatMetricValue = ({ hours, minutes, budget, count, quantity }) => {
    switch (metricType.value) {
      case METRIC.HOURS:
        return `${hours || 0}h ${minutes || 0}m`;
      case METRIC.TOTAL_AMOUNT:
        return `$${kFormatter(budget || 0)}`;
      case METRIC.COUNT: {
        const requestCount = count || 0;
        return `${requestCount} Request${requestCount === 1 ? "" : "s"}`;
      }
      case METRIC.QUANTITY: {
        return `${getQuantityTotal(quantity)} Quantity`;
      }
      default:
        return null;
    }
  };

  const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getDisplayLabel = (dateObj) => {
    if (year) {
      // In year mode, id is month index (0-11), display month name
      if (dateObj?.monthName) {
        return dateObj.monthName;
      }
      const monthIndex = Number.parseInt(dateObj?.id, 10);
      if (!Number.isNaN(monthIndex) && monthIndex >= 0 && monthIndex < 12) {
        return MONTH_NAMES[monthIndex];
      }
    }
    // In month mode, id is date string
    return dateObj?.id ? formatCardDate(dateObj.id) : "Unknown";
  };

  if (datesArray.length === 0) {
    return (
      <div className="allDates-container">
        <div className="allDates-header">
          <span className="allDates-span">All Dates</span>

          <button
            type="button"
            className="allDates-button"
            style={{ color: buttonColor, backgroundColor: hexToRgba(buttonColor, 0.12) }}
            onClick={handleViewAllClick}
          >
            View All
          </button>
        </div>

        <div className="no-dates-message">No dates available</div>
      </div>
    );
  }

  return (
    <div className="allDates-container">
      <div className="allDates-header">
        <span className="allDates-span">All Dates</span>

        <button
          type="button"
          className="allDates-button"
          style={{ color: buttonColor, backgroundColor: hexToRgba(buttonColor, 0.12) }}
          onClick={handleViewAllClick}
        >
          View All
        </button>
      </div>

      <div className="allDates-accordion">
        {datesArray.map((date, index) => {
          const dateId = date.id || date.title || index;
          const formattedData = formatCardDate(date.id);

          const isExpanded = expandedId === dateId;

          const metricValue = formatMetricValue({
            hours: date.hours,
            minutes: date.minutes,
            budget: date.budget,
            count: date.timesheetCount || date.count,
            quantity: date.quantity,
          });

          return (
            <div key={dateId} className="accordion-item expansionHeader">
              <button
                type="button"
                className="accordion-header"
                onClick={(e) => handleAction(dateId, e)}
                aria-expanded={isExpanded}
                style={{
                  // borderLeftColor: buttonColor,
                  borderLeft: `3px solid ${buttonColor}`,
                  backgroundColor: isExpanded ? hexToRgba(buttonColor, 0.12) : undefined,
                }}
              >
                <span className="accordion-title">{formattedData}</span>
                <span className="metric-value" style={{ color: buttonColor }}>
                  {metricValue}
                </span>
              </button>
              {isExpanded && (
                <div className="accordion-content">
                  {date?.contractTypes
                    ?.filter((item) => shouldDisplayMetric(item))
                    .map((item, index) => {
                      const accordionMetricValue = formatMetricValue({
                        hours: item.hours,
                        minutes: item.minutes,
                        budget: item.budget,
                        count: item.count,
                        quantity: item.quantity,
                      });

                      const contractTypeColor = getContractTypeColor(item.contract_type_name);

                      // Quantity: one flat row per breakdown entry,
                      // e.g. "On Call" ... "1 Game".
                      if (metricType.value === METRIC.QUANTITY && isQuantityBreakdown(item.quantity)) {
                        return Object.entries(item.quantity).map(([key, value]) => (
                          <button
                            key={`${item.contract_type_mstr_key}-${key}`}
                            className="accordion-expanded-content"
                            style={{ borderLeftColor: contractTypeColor }}
                            onClick={() =>
                              handleContractTypeClick(date, item.contract_type_mstr_key, { quantity_type: key })
                            }
                            type="button"
                          >
                            <div>{item.contract_type_name}</div>
                            <div style={{ color: contractTypeColor }}>
                              {value} {key.charAt(0).toUpperCase() + key.slice(1)}(s)
                            </div>
                          </button>
                        ));
                      }

                      return (
                        <button
                          key={item.contract_type_mstr_key}
                          className="accordion-expanded-content"
                          style={{ borderLeftColor: contractTypeColor }}
                          onClick={() => handleContractTypeClick(date, item.contract_type_mstr_key)}
                          type="button"
                        >
                          <div>{item.contract_type_name}</div>
                          <div style={{ color: contractTypeColor }}>{accordionMetricValue}</div>
                        </button>
                      );
                    })}

                  {/* {metricValue !== null && <div className="metric-value">{metricValue}</div>} */}
                  {date.content || date.details}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AllDates;
